// Use-Case Daten — gespeist aus dem Transkript & Produktblättern
import {
  BookOpenCheck, MicVocal, Video, Activity, SignalHigh, Rocket,
} from "lucide-react";

export const useCases = [
  {
    slug: "ki-wartungsassistent",
    icon: BookOpenCheck,
    title: `KI-Wartungsassistent per Sprache`,
    short: `Statt ölverschmierter Papier-Manuals: Der Techniker fragt – das Gerät antwortet.`,
    pain: `„Der Techniker geht ins Büro, druckt sich die Anleitung aus und nimmt sie mit auf die Anlage. Er kann weder Laptop noch Telefon mitnehmen. Also Papier. In Schweröl. Mit dicken Handschuhen. Es ist furchtbar."`,
    solution: [
      `Ein LLM läuft direkt am EdgeOne — kein Cloud-Zwang.`,
      `Bis zu 2,5 GB Ihrer Handbücher, Reparaturhistorie und Prozessbeschreibungen werden eingelesen.`,
      `Der Techniker stellt seine Frage per Sprache: „Wie wechsle ich das Dichtungsset an einer Yamada-Pumpe?"`,
      `EdgeOne antwortet in Sekunden — gesprochen oder am Display.`,
    ],
    benefits: [
      { metric: "−40 %", label: "kürzere Diagnosezeit pro Vorfall" },
      { metric: "0", label: "Papier-Manuals im Feld" },
      { metric: "24/7", label: "Wissen, das nie krank wird" },
    ],
    workflow: [
      `Wir importieren bis zu 800 Ihrer Dokumente in eine RAG-Wissensdatenbank.`,
      `EdgeOne kann auch offline antworten — ideal in Funklöchern und tiefen Anlagen.`,
      `Neue Dokumente werden zentral nachgepflegt und an die Flotte gepusht.`,
    ],
    image: "https://images.pexels.com/photos/12951623/pexels-photo-12951623.jpeg",
  },
  {
    slug: "sprachberichte",
    icon: MicVocal,
    title: `Sprachberichte ohne Tippen`,
    short: `Push-to-Form: Der Techniker erzählt — die KI füllt das Formular aus.`,
    pain: `Berichte am Anlagen-Ende sind die meistgehasste Aufgabe Ihrer Mitarbeiter. Handschuhe, Lärm, kleine Tasten, vergessene Felder — und am Ende Daten, mit denen niemand etwas anfangen kann.`,
    solution: [
      `Der Techniker drückt eine einzige Taste und spricht frei: „Ventil 4 vibriert, ich habe es nachgezogen, Restdruck 2,3 bar, Wiedervorlage in 6 Wochen."`,
      `Der KI-Agent extrahiert Felder, Werte und Befunde — komplett ohne Maske.`,
      `Fehlt eine Pflichtangabe, fragt der Agent gezielt nach.`,
      `Das fertige Protokoll landet in Ihrem CMMS, SAP PM oder Maximo.`,
    ],
    benefits: [
      { metric: "−70 %", label: "Schreibzeit pro Bericht" },
      { metric: "100 %", label: "Pflichtfelder befüllt" },
      { metric: "0", label: "Tippen im Feld" },
    ],
    workflow: [
      `Wir mappen Ihre bestehenden Formulare und Feldlogiken einmalig.`,
      `Spracherkennung läuft mehrsprachig, inkl. deutscher Fachbegriffe.`,
      `Audit-Log: Originale Sprachaufnahme wird DSGVO-konform 30 Tage aufbewahrt.`,
    ],
    image: "https://images.pexels.com/photos/3786157/pexels-photo-3786157.jpeg",
  },
  {
    slug: "remote-experte",
    icon: Video,
    title: `Remote-Experten-Support`,
    short: `Teams oder Zoom direkt am EdgeOne — der Experte sieht, was der Techniker sieht.`,
    pain: `Wenn der Techniker vor Ort nicht weiterkommt, kostet jeder zusätzliche Tag ohne Lösung schnell fünfstellig — und der Senior-Experte muss sich auf die nächste Reise vorbereiten.`,
    solution: [
      `8-MP-Kamera am EdgeOne plus Bluetooth-Headset oder Lautsprecher.`,
      `Microsoft Teams oder Zoom laufen nativ — keine zusätzliche Lizenz.`,
      `Optional in Verbindung mit RealWear-Brillen für komplett freie Hände.`,
      `Eigensicheres Push-to-Talk für Gruppenkommunikation in ATEX-Zonen.`,
    ],
    benefits: [
      { metric: "−60 %", label: "weniger Anreise-Einsätze" },
      { metric: "+30 %", label: "First-Time-Fix-Rate" },
      { metric: "5 Min.", label: "vom Anruf zum Experten-Bild" },
    ],
    workflow: [
      `Single-Sign-On mit Ihrem Microsoft- oder Google-Tenant.`,
      `Aufzeichnungen landen direkt in SharePoint oder Drive zur Schulung.`,
      `Eskalations-Workflows: Wenn niemand abnimmt, Anruf an Backup-Experten.`,
    ],
    image: "https://images.pexels.com/photos/8439094/pexels-photo-8439094.jpeg",
  },
  {
    slug: "iot-monitoring",
    icon: Activity,
    title: `IoT- & Sicherheits-Monitoring`,
    short: `Lärm, Vibration, Mann-Notruf, Herzfrequenz — kontinuierlich am Handgelenk Ihrer Anlage.`,
    pain: `90 % der relevanten Maschinen- und Umgebungsdaten landen heute nirgendwo. Ein Vibrationsanstieg an einer Pumpe? Ein Lärmpegel über Grenzwert? Eine Mitarbeiter-Ohnmacht? Niemand sieht es rechtzeitig.`,
    solution: [
      `EdgeOne sammelt Daten von Onboard-Sensoren (Vibration, Lärm, Bewegung) und von gekoppelten IoT-Geräten.`,
      `Schwellenwerte sind frei konfigurierbar — Warnung am Gerät und in Ihrem Dashboard.`,
      `Optionale Wearables liefern Herzfrequenz und Sauerstoffsättigung des Trägers.`,
      `Mann-Notruf bei Sturz, Bewusstlosigkeit oder zu hoher Belastung — automatisch.`,
    ],
    benefits: [
      { metric: "Echtzeit", label: "Lärm-/Vibrations-Alarme" },
      { metric: "<10 s", label: "Mann-Notruf-Auslösung" },
      { metric: "90 %", label: "Maschinendaten erstmals erfasst" },
    ],
    workflow: [
      `Schwellenwerte werden zentral verwaltet, an die Flotte gepusht.`,
      `Anbindung an Ihre OT-Plattform (Databricks, Azure IoT, AWS IoT, ThingWorx).`,
      `Predictive-Maintenance-Modelle auf Ihrer historischen Datenbasis trainierbar.`,
    ],
    image: "https://images.pexels.com/photos/36778676/pexels-photo-36778676.jpeg",
  },
  {
    slug: "private-5g",
    icon: SignalHigh,
    title: `Private 5G in jeder Anlage`,
    short: `Bring-Your-Own-Netz: zertifiziert mit Orange, Nokia und Ericsson.`,
    pain: `Öffentliches 5G endet am Werkstor. WLAN reicht nicht in den letzten Winkel der Anlage. Ohne stabile Konnektivität sind Live-Daten, Remote-Support und KI-Updates unmöglich.`,
    solution: [
      `EdgeOne unterstützt alle relevanten 5G-/4G-Bänder weltweit (n1–n79).`,
      `Public und Private 5G — zertifiziert mit Orange, Nokia, Ericsson.`,
      `Wi-Fi 6 & 6E, BT 5.2, NFC — eSIM + Nano-SIM gleichzeitig betreibbar.`,
      `EdgeOne wirkt selbst als Hotspot für bis zu 32 weitere Geräte.`,
    ],
    benefits: [
      { metric: "n1–n79", label: "5G-Bänder weltweit" },
      { metric: "32", label: "Geräte pro EdgeOne-Hotspot" },
      { metric: "0", label: "Funklöcher im Werk" },
    ],
    workflow: [
      `Wir helfen bei Frequenzanmeldung und Roll-out Ihres Private 5G.`,
      `Edge-Verarbeitung läuft auch ohne Backhaul: Daten landen synchronisiert in der Cloud, sobald Netz da ist.`,
      `Roaming zwischen Public und Private 5G ist nahtlos.`,
    ],
    image: "https://images.pexels.com/photos/163170/board-printed-circuit-board-computer-electronics-163170.jpeg",
  },
  {
    slug: "pionier-programm",
    icon: Rocket,
    title: `Pionier-Programm — KI im Feld in 4 Wochen`,
    short: `5 Geräte, 2,5 GB Ihrer Dokumente, ein funktionsfähiger KI-Agent — schlüsselfertig.`,
    pain: `KI-Projekte scheitern selten an der Technologie, sondern an Komplexität, Beschaffung und Time-to-Value. Sie wollen den Use Case beweisen — bevor Sie die Flotte ausrollen.`,
    solution: [
      `5× EdgeOne inkl. eigensicheres Bluetooth-Headset, Zusatzakku und Tischladestation.`,
      `Wir importieren bis zu 800 Ihrer Dokumente (max. 2,5 GB) in die RAG-Wissensdatenbank.`,
      `ALEGER konfiguriert Geräte, schult Ihre Techniker und begleitet den Piloten.`,
      `Fester Preis. Festes Ergebnis. 8 Wochen Laufzeit.`,
    ],
    benefits: [
      { metric: "5", label: "Geräte im Piloten" },
      { metric: "4–8", label: "Wochen bis Go-Live" },
      { metric: "1", label: "Festpreis, keine Überraschungen" },
    ],
    workflow: [
      `Kick-off & Use-Case-Workshop (Woche 1).`,
      `Dokumenten-Import + Geräte-Konfiguration (Woche 2–3).`,
      `Schulung & Pilot-Einsatz auf Ihrer Anlage (Woche 4–8).`,
      `Ergebnisbericht + Business Case für Flotten-Rollout.`,
    ],
    image: "https://images.pexels.com/photos/3862632/pexels-photo-3862632.jpeg",
  },
];

export const useCaseBySlug = (slug) => useCases.find((u) => u.slug === slug);
